import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  TrendingUp,
  FileText,
  Users,
  CreditCard
} from 'lucide-react';
import { ClientService } from '../../services/clients/clientService';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getUserDisplayName } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';

interface DashboardStats {
  factures_en_attente: number;
  nombre_fournisseurs_actifs: number;
  volume_mensuel: number;
  taux_validation: number;
}

export const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const clientId = user?.client_profile_id;
      if (!clientId) {
        setLoading(false);
        return;
      }
      try {
        const statsRes = await ClientService.getClientStats(clientId);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data", error);
        toast.error(t('common.errorGroup'));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  if (loading) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  const statCards = [
    {
      name: t('dashboard.client.pendingInvoices'),
      value: stats?.factures_en_attente || 0,
      change: '0',
      changeType: 'neutral',
      icon: FileText,
      color: 'blue'
    },
    {
      name: t('dashboard.client.activeSuppliers'),
      value: stats?.nombre_fournisseurs_actifs || 0,
      change: '0',
      changeType: 'neutral',
      icon: Users,
      color: 'green'
    },
    {
      name: t('dashboard.client.monthlyVolume'),
      value: stats?.volume_mensuel 
        ? new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(stats.volume_mensuel)
        : '0 FCFA',
      change: '0%', // Backend doesn't provide change yet
      changeType: 'neutral',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      name: t('dashboard.client.validationRate'),
      value: `${stats?.taux_validation}%` || '0%',
      change: '0%',
      changeType: 'neutral',
      icon: CreditCard,
      color: 'yellow'
    }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('header.welcome')}, {getUserDisplayName(user)}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {t('dashboard.client.viewOverview')}
        </p>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg ${
                      stat.color === 'blue' ? 'bg-blue-100' :
                      stat.color === 'green' ? 'bg-green-100' :
                      stat.color === 'purple' ? 'bg-purple-100' :
                      'bg-yellow-100'
                    }`}>
                      <Icon className={`h-6 w-6 ${
                        stat.color === 'blue' ? 'text-blue-600' :
                        stat.color === 'green' ? 'text-green-600' :
                        stat.color === 'purple' ? 'text-purple-600' :
                        'text-yellow-600'
                      }`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Actions rapides */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t('dashboard.quickActions.title')}
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <Link to="/client/fournisseurs?inviter=1" className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
              {t('dashboard.quickActions.inviteSupplier')}
            </Link>
            <Link to="/client/factures" className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
              {t('dashboard.quickActions.viewPendingInvoices')}
            </Link>
            <Link to="/client/analytics" className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
              {t('dashboard.quickActions.generateReport')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}